import React, { useEffect, useMemo, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Paper, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiHost } from '../../utils/apiHost';
import { exportStyledExcel } from '../../utils/exportHelpers';
import { barisBuktiPiutang, tanggalIndo } from '../../utils/uangMuka';
import { ambilKonfigStruk, barisStrukKasir } from '../../struk/strukWeb';
import { cetakBrowser } from '../../struk/cetakStruk';

// Piutang pelanggan (uang muka / pembayaran bertahap): daftar yang belum lunas, terima pembayaran, hapus sisa (admin),
// cetak bukti, dan laporan arus piutang. Server: controller/piutangController.js (/piutang, /piutang/laporan, ...).
const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const pesanGalat = (e) => e?.response?.data?.message || e?.message || 'Gagal';
const tgl = (d) => { const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const namaMetode = (p) => (p.metode === 'cash' ? 'Tunai' : p.nonCashMethodName || 'Non-tunai');
const NAMA_JENIS = { bayar: 'Pembayaran', hapus: 'Sisa dihapus', kembali: 'Dikembalikan' };

const Kartu = ({ judul, nilai, warna = 'text.primary', catatan }) => (
  <Card variant="outlined">
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{judul}</Typography>
      <Typography variant="h6" color={warna} fontWeight={700}>{nilai}</Typography>
      {catatan && <Typography variant="caption" color="text.secondary">{catatan}</Typography>}
    </CardContent>
  </Card>
);

const STATUS_PELANGGAN = {
  belum: { label: 'Belum lunas', color: 'warning' },
  lewat: { label: 'Lewat tempo', color: 'error' },
  lunas: { label: 'Lunas', color: 'success' },
};
const NAMA_FILTER_JENIS = { semua: 'Semua jenis', baru: 'Piutang baru', bayar: 'Pembayaran masuk', hapus: 'Sisa dihapus', kembali: 'Dikembalikan' };
const NAMA_RIWAYAT = { dp: 'Uang muka', bayar: 'Pembayaran', hapus: 'Sisa dihapus', kembali: 'Dikembalikan' };

const Piutang = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const bolehHapus = user?.role === 'admin' || superadmin;
  const { mutate: mutateSemua } = useSWRConfig();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('belum'); // belum | lewat | lunas | semua
  const [cabang, setCabang] = useState('');
  const [pelangganUuid, setPelangganUuid] = useState(''); // halaman satu pelanggan
  const [detailUuid, setDetailUuid] = useState('');
  const [bayarUuid, setBayarUuid] = useState('');
  const [hapusUuid, setHapusUuid] = useState('');
  const [pesan, setPesan] = useState(null);

  const query = new URLSearchParams({ ...(q && { q }), status, ...(cabang && { cabang }) }).toString();
  const { data, error, isLoading } = useSWR(`${base()}/piutang?${query}`, ambil, { revalidateOnFocus: true });
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];
  const ringkasan = data?.data?.ringkasan;
  const pelanggan = data?.data?.pelanggan || [];

  // Setelah ada perubahan (bayar / hapus sisa): muat ulang daftar dan halaman pelanggan.
  const selesai = (teks) => {
    setPesan({ jenis: 'success', teks });
    mutateSemua((k) => typeof k === 'string' && k.includes('/piutang'));
  };

  const dialog = (
    <>
      {detailUuid && (
        <DialogDetail
          uuid={detailUuid}
          user={user}
          bolehHapus={bolehHapus}
          onClose={() => setDetailUuid('')}
          onBayar={(u) => { setDetailUuid(''); setBayarUuid(u); }}
          onHapus={(u) => { setDetailUuid(''); setHapusUuid(u); }}
        />
      )}
      {bayarUuid && <DialogBayar uuid={bayarUuid} onClose={() => setBayarUuid('')} onSelesai={(t) => { setBayarUuid(''); selesai(t); }} />}
      {hapusUuid && <DialogHapus uuid={hapusUuid} onClose={() => setHapusUuid('')} onSelesai={(t) => { setHapusUuid(''); selesai(t); }} />}
      <Snackbar open={!!pesan} autoHideDuration={5000} onClose={() => setPesan(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {pesan ? <Alert severity={pesan.jenis} onClose={() => setPesan(null)}>{pesan.teks}</Alert> : <span />}
      </Snackbar>
    </>
  );

  if (pelangganUuid) {
    return (
      <Box sx={{ p: 2 }}>
        <HalamanPelanggan
          customerUuid={pelangganUuid}
          onKembali={() => setPelangganUuid('')}
          onDetail={setDetailUuid}
          onBayar={setBayarUuid}
        />
        {dialog}
      </Box>
    );
  }

  const adaStatusSelesai = status === 'lunas' || status === 'semua';
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Piutang Pelanggan</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Satu baris per pelanggan yang pernah membayar uang muka (DP). Klik nama pelanggan untuk melihat transaksi dan riwayat pembayarannya.
        Penjualan diakui penuh saat dibuat; uang masuk dicatat pada tanggal diterima.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {adaStatusSelesai ? (
          <>
            <Kartu judul="Total belanja" nilai={rupiah(ringkasan?.totalBelanja)} />
            <Kartu judul="Sudah dibayar" nilai={rupiah(ringkasan?.totalDibayar)} warna="success.main" />
            <Kartu judul="Masih piutang" nilai={rupiah(ringkasan?.totalPiutang)} warna="warning.dark" />
            <Kartu judul="Pelanggan" nilai={ringkasan?.jumlahPelanggan ?? 0} catatan={`${ringkasan?.jumlahTransaksi ?? 0} transaksi • ${ringkasan?.jumlahLunas ?? 0} selesai`} />
          </>
        ) : (
          <>
            <Kartu judul="Total piutang" nilai={rupiah(ringkasan?.totalPiutang)} warna="warning.dark" />
            <Kartu judul="Pelanggan" nilai={ringkasan?.jumlahPelanggan ?? 0} />
            <Kartu judul="Transaksi" nilai={ringkasan?.jumlahTransaksi ?? 0} />
            <Kartu judul="Lewat jatuh tempo" nilai={rupiah(ringkasan?.lewatTempo?.total)} warna={ringkasan?.lewatTempo?.jumlah ? 'error.main' : 'text.primary'} catatan={`${ringkasan?.lewatTempo?.jumlah ?? 0} transaksi`} />
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField size="small" label="Cari pelanggan / order" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }} />
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)}>
          <ToggleButton value="belum">Belum lunas</ToggleButton>
          <ToggleButton value="lewat">Lewat tempo</ToggleButton>
          <ToggleButton value="lunas">Lunas</ToggleButton>
          <ToggleButton value="semua">Semua</ToggleButton>
        </ToggleButtonGroup>
        {superadmin && (
          <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => setCabang(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Semua cabang</MenuItem>
            {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </TextField>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{pesanGalat(error)}</Alert>}
      {isLoading && <Typography>Memuat…</Typography>}
      {!isLoading && !error && pelanggan.length === 0 && (
        <Alert severity={adaStatusSelesai ? 'info' : 'success'}>
          {status === 'lunas' ? 'Belum ada transaksi yang lunas.' : status === 'semua' ? 'Belum ada transaksi dengan uang muka.' : status === 'lewat' ? 'Tidak ada piutang yang lewat jatuh tempo.' : 'Tidak ada piutang yang belum lunas. Yang sudah lunas ada di pilihan "Lunas".'}
        </Alert>
      )}
      {pelanggan.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pelanggan</TableCell>
                <TableCell>Telepon</TableCell>
                <TableCell align="right">Transaksi</TableCell>
                <TableCell align="right">Total belanja</TableCell>
                <TableCell align="right">Sudah dibayar</TableCell>
                <TableCell align="right">Sisa</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pelanggan.map((p) => {
                const st = STATUS_PELANGGAN[p.status] || STATUS_PELANGGAN.belum;
                return (
                  <TableRow key={p.customerUuid || '-'} hover sx={{ cursor: p.customerUuid ? 'pointer' : 'default' }} onClick={() => p.customerUuid && setPelangganUuid(p.customerUuid)}>
                    <TableCell><Typography fontWeight={600} color="primary">{p.nama}</Typography></TableCell>
                    <TableCell>{p.telepon || '-'}</TableCell>
                    <TableCell align="right">{p.jumlahTransaksi}</TableCell>
                    <TableCell align="right">{rupiah(p.totalBelanja)}</TableCell>
                    <TableCell align="right">{rupiah(p.totalDibayar)}</TableCell>
                    <TableCell align="right"><b>{rupiah(p.totalSisa)}</b></TableCell>
                    <TableCell><Chip size="small" color={st.color} label={st.label} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {dialog}
    </Box>
  );
};

/** Halaman satu pelanggan: ringkasan, semua transaksi uang muka (termasuk lunas) dan riwayat pembayaran berurutan. */
const HalamanPelanggan = ({ customerUuid, onKembali, onDetail, onBayar }) => {
  const { data, error, isLoading } = useSWR(`${base()}/piutang/pelanggan/${customerUuid}`, ambil, { revalidateOnFocus: true });
  const d = data?.data;
  const r = d?.ringkasan;
  const st = STATUS_PELANGGAN[r?.status] || STATUS_PELANGGAN.belum;
  return (
    <>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={onKembali} sx={{ mb: 1 }}>Kembali ke daftar pelanggan</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{pesanGalat(error)}</Alert>}
      {isLoading && <Typography>Memuat…</Typography>}
      {d && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="h6">{d.pelanggan.nama}</Typography>
            <Chip size="small" color={st.color} label={st.label} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {d.pelanggan.telepon || 'Tanpa telepon'}{d.pelanggan.alamat ? ` • ${d.pelanggan.alamat}` : ''}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            <Kartu judul="Total belanja" nilai={rupiah(r.totalBelanja)} catatan={`${r.jumlahTransaksi} transaksi`} />
            <Kartu judul="Sudah dibayar" nilai={rupiah(r.totalDibayar)} warna="success.main" />
            <Kartu judul="Sisa piutang" nilai={rupiah(r.totalSisa)} warna={r.totalSisa > 0 ? 'warning.dark' : 'success.main'} />
            <Kartu judul="Lewat jatuh tempo" nilai={rupiah(r.lewatTempo.total)} warna={r.lewatTempo.jumlah ? 'error.main' : 'text.primary'} catatan={`${r.lewatTempo.jumlah} transaksi`} />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Transaksi</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell><TableCell>Tanggal</TableCell>
                  <TableCell align="right">Total</TableCell><TableCell align="right">Dibayar</TableCell><TableCell align="right">Sisa</TableCell>
                  <TableCell>Jatuh tempo / status</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {d.transaksi.map((t) => (
                  <TableRow key={t.uuid} hover>
                    <TableCell>{t.order_id}</TableCell>
                    <TableCell>{tanggalIndo(t.tanggal)}</TableCell>
                    <TableCell align="right">{rupiah(t.totaljual)}</TableCell>
                    <TableCell align="right">{rupiah(t.dibayar)}</TableCell>
                    <TableCell align="right"><b>{rupiah(t.sisaTagihan)}</b></TableCell>
                    <TableCell>
                      {t.status === 'lunas' && <Chip size="small" color="success" label={`Lunas${t.lunasPada ? ` ${tanggalIndo(t.lunasPada)}` : ''}`} />}
                      {t.status === 'dihapus' && <Chip size="small" label={`Sisa dihapus${t.lunasPada ? ` ${tanggalIndo(t.lunasPada)}` : ''}`} />}
                      {t.status === 'belum' && (t.jatuhTempo ? tanggalIndo(t.jatuhTempo) : '-')}
                      {t.lewatTempo && <Chip size="small" color="error" label="Lewat" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" onClick={() => onDetail(t.uuid)}>Detail</Button>
                      {t.status === 'belum' && <Button size="small" variant="contained" onClick={() => onBayar(t.uuid)}>Terima bayar</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Riwayat pembayaran</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tanggal</TableCell><TableCell>Order</TableCell><TableCell>Jenis</TableCell><TableCell>Metode</TableCell>
                  <TableCell align="right">Jumlah</TableCell><TableCell>Catatan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {d.riwayat.map((x, i) => (
                  <TableRow key={`${x.transaksiuuid}-${i}`}>
                    <TableCell>{tanggalIndo(x.tanggal)}</TableCell>
                    <TableCell>{x.order_id}</TableCell>
                    <TableCell>{NAMA_RIWAYAT[x.jenis] || x.jenis}</TableCell>
                    <TableCell>{x.metode ? namaMetode(x) : '-'}</TableCell>
                    <TableCell align="right">{x.jenis === 'hapus' || x.jenis === 'kembali' ? '−' : ''}{rupiah(x.jumlah)}</TableCell>
                    <TableCell>{x.catatan || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
};

/** Cetak bukti pembayaran piutang: struk yang sama dengan struk kasir, dengan rincian DP & pembayaran bertahap. */
export const cetakBuktiPiutang = async (detail, user) => {
  const { cabangConfig, logoUrl } = await ambilKonfigStruk(axios, base(), user?.cabang?.uuid || detail.cabanguuid);
  const receipt = {
    items: (detail.items || []).map((i) => ({ name: i.nama, price: i.harga, quantity: i.jumlah })),
    total: detail.totaljual,
    paymentMethod: 'DP',
    paymentLines: barisBuktiPiutang(detail),
    namaCustomer: detail.customer?.nama || 'Pelanggan',
    createdAt: detail.tanggal,
    orderId: detail.order_id,
  };
  cetakBrowser(barisStrukKasir({
    receipt,
    cabangConfig,
    namaCabang: user?.cabang?.namacabang || 'Kasir',
    kasir: user?.nama || 'Kasir',
    logoUrl,
  }));
};

const DialogDetail = ({ uuid, user, bolehHapus, onClose, onBayar, onHapus }) => {
  const { data, error } = useSWR(`${base()}/piutang/transaksi/${uuid}`, ambil);
  const d = data?.data;
  const sisa = Number(d?.sisaTagihan) || 0;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Detail piutang {d?.order_id}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{pesanGalat(error)}</Alert>}
        {!d && !error && <Typography>Memuat…</Typography>}
        {d && (
          <>
            <Typography variant="body2" color="text.secondary">
              {d.customer?.nama || 'Pelanggan'} {d.customer?.telepon ? `• ${d.customer.telepon}` : ''} • {tanggalIndo(d.tanggal)}{d.kasir ? ` • kasir ${d.kasir.nama}` : ''}
            </Typography>
            <Table size="small" sx={{ my: 1 }}>
              <TableBody>
                {d.items.map((i) => (
                  <TableRow key={i.uuid}>
                    <TableCell>{i.nama} × {i.jumlah}</TableCell>
                    <TableCell align="right">{rupiah(i.total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow><TableCell><b>Total</b></TableCell><TableCell align="right"><b>{rupiah(d.totaljual)}</b></TableCell></TableRow>
              </TableBody>
            </Table>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Riwayat pembayaran</Typography>
            <Table size="small">
              <TableBody>
                {d.uangMuka.map((b, i) => (
                  <TableRow key={`dp${i}`}>
                    <TableCell>{tanggalIndo(d.tanggal)} • Uang muka ({namaMetode(b)})</TableCell>
                    <TableCell align="right">{rupiah(b.nominal)}</TableCell>
                  </TableRow>
                ))}
                {d.pembayaran.map((p) => (
                  <TableRow key={p.uuid}>
                    <TableCell>
                      {tanggalIndo(p.tanggal)} • {NAMA_JENIS[p.jenis] || p.jenis}{p.jenis === 'bayar' ? ` (${namaMetode(p)})` : ''}
                      {p.catatan && <Typography variant="caption" color="text.secondary" display="block">{p.catatan}</Typography>}
                    </TableCell>
                    <TableCell align="right">{p.jenis === 'bayar' ? '' : '−'}{rupiah(p.jumlah)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
              <Typography>Sisa tagihan</Typography>
              <Typography fontWeight={700} color={sisa > 0 ? 'warning.dark' : 'success.main'}>{sisa > 0 ? rupiah(sisa) : 'LUNAS'}</Typography>
            </Box>
            {d.jatuhTempo && <Typography variant="caption" color="text.secondary">Jatuh tempo {tanggalIndo(d.jatuhTempo)}</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Tutup</Button>
        {d && <Button onClick={() => cetakBuktiPiutang(d, user)}>Cetak bukti</Button>}
        {d && sisa > 0 && bolehHapus && <Button color="error" onClick={() => onHapus(uuid)}>Hapus sisa</Button>}
        {d && sisa > 0 && <Button variant="contained" onClick={() => onBayar(uuid)}>Terima bayar</Button>}
      </DialogActions>
    </Dialog>
  );
};

const DialogBayar = ({ uuid, onClose, onSelesai }) => {
  const { data } = useSWR(`${base()}/piutang/transaksi/${uuid}`, ambil);
  const { data: metodeData } = useSWR(`${base()}/getnoncashmethod`, ambil);
  const metodeNon = Array.isArray(metodeData?.data) ? metodeData.data : [];
  const d = data?.data;
  const sisa = Number(d?.sisaTagihan) || 0;
  const [jumlah, setJumlah] = useState('');
  const [metode, setMetode] = useState('cash');
  const [namaNon, setNamaNon] = useState('');
  const [diterima, setDiterima] = useState('');
  const [catatan, setCatatan] = useState('');
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const clientId = useMemo(() => `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`, []);
  const nilai = jumlah === '' ? sisa : Number(jumlah) || 0;
  const kembalian = metode === 'cash' && diterima !== '' ? Math.max(0, Number(diterima) - nilai) : 0;

  const kirim = async () => {
    setProses(true); setGalat('');
    try {
      const body = { jumlah: nilai, metode, clientId, ...(catatan && { catatan }) };
      if (metode === 'noncash') body.nonCashMethodName = namaNon;
      if (metode === 'cash' && diterima !== '') body.diterima = Number(diterima);
      const r = await axios.post(`${base()}/piutang/${uuid}/bayar`, body, { withCredentials: true });
      onSelesai(r.data?.message || 'Pembayaran tercatat');
    } catch (e) {
      setGalat(pesanGalat(e));
      setProses(false);
    }
  };

  return (
    <Dialog open onClose={proses ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Terima pembayaran {d?.order_id}</DialogTitle>
      <DialogContent dividers>
        {!d && <Typography>Memuat…</Typography>}
        {d && (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Typography variant="body2">{d.customer?.nama} • sisa tagihan <b>{rupiah(sisa)}</b></Typography>
            <TextField
              size="small" type="number" label="Jumlah dibayar" value={jumlah} placeholder={String(Math.round(sisa))}
              onChange={(e) => setJumlah(e.target.value)} helperText="Kosongkan untuk melunasi seluruh sisa"
            />
            <TextField size="small" select label="Metode" value={metode} onChange={(e) => setMetode(e.target.value)}>
              <MenuItem value="cash">Tunai</MenuItem>
              <MenuItem value="noncash">Non-tunai</MenuItem>
            </TextField>
            {metode === 'noncash' && (
              <TextField size="small" select label="Metode non-tunai" value={namaNon} onChange={(e) => setNamaNon(e.target.value)}>
                {metodeNon.length === 0 && <MenuItem value="" disabled>Belum ada metode, hubungi admin</MenuItem>}
                {metodeNon.map((m) => <MenuItem key={m.uuid} value={m.name}>{m.name}</MenuItem>)}
              </TextField>
            )}
            {metode === 'cash' && (
              <>
                <TextField size="small" type="number" label="Uang diterima (opsional)" value={diterima} onChange={(e) => setDiterima(e.target.value)} />
                {kembalian > 0 && <Typography variant="body2" color="success.main">Kembalian {rupiah(kembalian)}</Typography>}
              </>
            )}
            <TextField size="small" label="Catatan (opsional)" value={catatan} onChange={(e) => setCatatan(e.target.value)} />
            {galat && <Alert severity="error">{galat}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button variant="contained" onClick={kirim} disabled={!d || proses || nilai <= 0 || (metode === 'noncash' && !namaNon)}>
          {proses ? 'Menyimpan…' : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogHapus = ({ uuid, onClose, onSelesai }) => {
  const [alasan, setAlasan] = useState('');
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const kirim = async () => {
    setProses(true); setGalat('');
    try {
      const r = await axios.post(`${base()}/piutang/${uuid}/hapus-sisa`, { alasan }, { withCredentials: true });
      onSelesai(r.data?.message || 'Sisa piutang dihapus');
    } catch (e) {
      setGalat(pesanGalat(e));
      setProses(false);
    }
  };
  return (
    <Dialog open onClose={proses ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Hapus sisa piutang</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 1.5 }}>Sisa tagihan dianggap lunas tanpa uang masuk. Tindakan ini tercatat dan tidak bisa dibatalkan.</Alert>
        <TextField fullWidth size="small" label="Alasan (wajib)" value={alasan} onChange={(e) => setAlasan(e.target.value)} />
        {galat && <Alert severity="error" sx={{ mt: 1.5 }}>{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button color="error" variant="contained" onClick={kirim} disabled={proses || alasan.trim().length < 3}>Hapus sisa</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Halaman Laporan Piutang (menu Laporan): arus piutang pada rentang tanggal + ekspor Excel. */
export const LaporanPiutangHalaman = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Laporan Piutang</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Piutang baru, pembayaran yang masuk (per metode, menurut tanggal diterima), sisa yang dihapus, dan uang yang dikembalikan.
      </Typography>
      <LaporanPiutang superadmin={superadmin} daftarCabang={daftarCabang} />
    </Box>
  );
};

const LaporanPiutang = ({ superadmin, daftarCabang }) => {
  const [dari, setDari] = useState(() => { const d = new Date(); d.setDate(1); return tgl(d); });
  const [sampai, setSampai] = useState(() => tgl(new Date()));
  const [cabang, setCabang] = useState('');
  const [cari, setCari] = useState('');
  const [q, setQ] = useState(''); // kata cari yang sudah dikirim ke server (ditunda supaya tidak tiap ketikan)
  const [jenis, setJenis] = useState('semua'); // semua | baru | bayar | hapus | kembali
  const [metode, setMetode] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(cari.trim()), 400);
    return () => clearTimeout(t);
  }, [cari]);
  const query = new URLSearchParams({ dari, sampai, ...(cabang && { cabang }), ...(q && { q }), ...(jenis !== 'semua' && { jenis }), ...(metode && { metode }) }).toString();
  const { data, error, isLoading } = useSWR(`${base()}/piutang/laporan?${query}`, ambil, { revalidateOnFocus: false });
  const l = data?.data;
  const r = l?.ringkasan;
  const opsiMetode = l?.opsiMetode || [];
  const adaFilter = !!(q || cari || jenis !== 'semua' || metode);
  const tampilBaru = jenis === 'semua' || jenis === 'baru';
  const tampilBayar = jenis !== 'baru';
  const resetFilter = () => { setCari(''); setQ(''); setJenis('semua'); setMetode(''); };

  const unduh = async () => {
    const metode = Object.entries(r?.pembayaranPerMetode || {});
    await exportStyledExcel({
      fileName: `laporan-piutang-${dari}-${sampai}.xlsx`,
      sheetName: 'Piutang',
      title: 'Laporan Piutang Pelanggan',
      metaRows: [['Periode', `${tanggalIndo(dari)} - ${tanggalIndo(sampai)}`], ...(adaFilter ? [['Filter', [q && `cari "${q}"`, jenis !== 'semua' && `jenis ${NAMA_FILTER_JENIS[jenis]}`, metode && `metode ${metode}`].filter(Boolean).join(', ')]] : []), ['Pembayaran masuk', rupiah(r?.pembayaranMasuk)], ['Dihapus', rupiah(r?.dihapus)], ['Dikembalikan', rupiah(r?.dikembalikan)], ...metode.map(([m, v]) => [`Masuk via ${m}`, rupiah(v)])],
      headers: ['Tanggal', 'Jenis', 'Order / Pelanggan', 'Metode', 'Jumlah', 'Catatan'],
      rows: [
        ...(l?.piutangBaru || []).map((x) => [tanggalIndo(x.tanggal), 'Piutang baru', `${x.order_id} • ${x.pelanggan || '-'}`, '-', x.piutangAwal, `Total ${x.totaljual}, DP ${x.uangMuka}`]),
        ...(l?.pembayaran || []).map((p) => [tanggalIndo(p.tanggal), NAMA_JENIS[p.jenis] || p.jenis, `${p.order_id || p.transaksiuuid} • ${p.pelanggan || '-'}`, p.metode ? namaMetode(p) : '-', p.jumlah, p.catatan || '']),
      ],
      columnWidths: [16, 18, 40, 18, 16, 36],
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" type="date" label="Dari" InputLabelProps={{ shrink: true }} value={dari} onChange={(e) => setDari(e.target.value)} />
        <TextField size="small" type="date" label="Sampai" InputLabelProps={{ shrink: true }} value={sampai} onChange={(e) => setSampai(e.target.value)} />
        {superadmin && (
          <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => setCabang(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Semua cabang</MenuItem>
            {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </TextField>
        )}
        <Button variant="outlined" onClick={unduh} disabled={!l}>Unduh Excel</Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField size="small" label="Cari pelanggan / order" value={cari} onChange={(e) => setCari(e.target.value)} sx={{ minWidth: 240 }} />
        <TextField size="small" select label="Jenis" value={jenis} onChange={(e) => setJenis(e.target.value)} sx={{ minWidth: 180 }}>
          {Object.entries(NAMA_FILTER_JENIS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Metode pembayaran" value={metode} onChange={(e) => setMetode(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">Semua metode</MenuItem>
          {opsiMetode.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          {metode && !opsiMetode.includes(metode) && <MenuItem value={metode}>{metode}</MenuItem>}
        </TextField>
        {adaFilter && <Button size="small" onClick={resetFilter}>Reset filter</Button>}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{pesanGalat(error)}</Alert>}
      {isLoading && <Typography>Memuat…</Typography>}
      {r && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            <Kartu judul="Piutang baru" nilai={rupiah(r.piutangBaru.total)} warna="warning.dark" catatan={`${r.piutangBaru.jumlah} transaksi`} />
            <Kartu judul="Pembayaran masuk" nilai={rupiah(r.pembayaranMasuk)} warna="success.main" />
            <Kartu judul="Sisa dihapus" nilai={rupiah(r.dihapus)} />
            <Kartu judul="Dikembalikan" nilai={rupiah(r.dikembalikan)} />
          </Box>
          {Object.keys(r.pembayaranPerMetode).length > 0 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Masuk per metode: {Object.entries(r.pembayaranPerMetode).map(([m, v]) => `${m} ${rupiah(v)}`).join(' • ')}
            </Typography>
          )}
          {tampilBaru && (
            <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Piutang baru</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Tanggal</TableCell><TableCell>Order</TableCell><TableCell>Pelanggan</TableCell><TableCell align="right">Total</TableCell><TableCell align="right">Uang muka</TableCell><TableCell align="right">Piutang</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {l.piutangBaru.length === 0 && <TableRow><TableCell colSpan={6}>{adaFilter ? 'Tidak ada piutang baru yang cocok dengan filter.' : 'Tidak ada piutang baru pada rentang ini.'}</TableCell></TableRow>}
                {l.piutangBaru.map((x) => (
                  <TableRow key={x.uuid}><TableCell>{tanggalIndo(x.tanggal)}</TableCell><TableCell>{x.order_id}</TableCell><TableCell>{x.pelanggan || '-'}</TableCell><TableCell align="right">{rupiah(x.totaljual)}</TableCell><TableCell align="right">{rupiah(x.uangMuka)}</TableCell><TableCell align="right"><b>{rupiah(x.piutangAwal)}</b></TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
            </>
          )}
          {tampilBayar && (
            <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Pembayaran & penyesuaian</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Tanggal</TableCell><TableCell>Pelanggan</TableCell><TableCell>Order</TableCell><TableCell>Jenis</TableCell><TableCell>Metode</TableCell><TableCell align="right">Jumlah</TableCell><TableCell>Catatan</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {l.pembayaran.length === 0 && <TableRow><TableCell colSpan={7}>{adaFilter ? 'Tidak ada pembayaran yang cocok dengan filter.' : 'Belum ada pembayaran pada rentang ini.'}</TableCell></TableRow>}
                {l.pembayaran.map((p) => (
                  <TableRow key={p.uuid}><TableCell>{tanggalIndo(p.tanggal)}</TableCell><TableCell>{p.pelanggan || '-'}</TableCell><TableCell>{p.order_id || '-'}</TableCell><TableCell>{NAMA_JENIS[p.jenis] || p.jenis}</TableCell><TableCell>{p.metode ? namaMetode(p) : '-'}</TableCell><TableCell align="right">{rupiah(p.jumlah)}</TableCell><TableCell>{p.catatan || ''}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
            </>
          )}
        </>
      )}
    </>
  );
};

export default Piutang;
